import { HttpClient } from '@angular/common/http';
import { inject, Injectable, type Signal } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { ChangePassword } from '../../shared/components/change-password/change-password.model';
import { CredentialToken, SessionResponse, WebLogin } from '../authentication/models';
import { AuthenticatedUser, UserGroup } from '../authorization/models';
import { ApiCacheService } from '../services';

/**
 * Response shape from the login API endpoint.
 */
interface LoginResponse {
  readonly user: AuthenticatedUser['appUser'];
  readonly accessToken: string;
  readonly expiresIn: number;
}

/**
 * API service for security-related operations.
 * Handles authentication, authorization, and token management.
 */
@Injectable({ providedIn: 'root' })
export class SecurityApiService {
  private readonly http = inject(HttpClient);
  private readonly cacheService = inject(ApiCacheService);
  private readonly baseUrl = 'api/security';
  private readonly authUrl = 'api/auth';

  /** Signal containing cached user groups data */
  readonly userGroupsData: Signal<readonly UserGroup[]> = this.cacheService.userGroupsData;

  /** Signal indicating if user groups are loading */
  readonly userGroupsLoading: Signal<boolean> = this.cacheService.userGroupsLoading;

  /**
   * Authenticates a user with email and password.
   * The server sets an HttpOnly refresh token cookie automatically.
   * @param webLogin - Login credentials
   * @returns Observable of authenticated user with token expiration
   */
  login(webLogin: WebLogin): Observable<{ user: AuthenticatedUser; expiresIn: number }> {
    return this.http
      .post<LoginResponse>(`${this.baseUrl}/login`, webLogin, {
        withCredentials: true, // Allow server to set HttpOnly cookie
      })
      .pipe(
        map((res) => ({
          user: {
            appUser: res.user,
            token: res.accessToken,
          },
          expiresIn: res.expiresIn,
        })),
      );
  }

  /**
   * Authenticates a rater using their unique GUID.
   * The server sets an HttpOnly refresh token cookie automatically.
   * @param guid - Rater's unique identifier
   * @returns Observable of authenticated user with token expiration
   */
  raterLogin(guid: string): Observable<{ user: AuthenticatedUser; expiresIn: number }> {
    return this.http
      .get<LoginResponse>(`${this.baseUrl}/rater/login/${guid}`, {
        withCredentials: true, // Allow server to set HttpOnly cookie
      })
      .pipe(
        map((res) => ({
          user: {
            appUser: res.user,
            token: res.accessToken,
          },
          expiresIn: res.expiresIn,
        })),
      );
  }

  /**
   * Retrieves available user groups with their resources and permissions.
   * Results are cached to prevent redundant API calls.
   * @param forceRefresh - Force a fresh fetch even if cached
   * @returns Observable of user groups array
   */
  getUserGroups(forceRefresh = false): Observable<readonly UserGroup[]> {
    return this.cacheService.getUserGroups(forceRefresh);
  }

  /**
   * Refreshes the authentication token using the HttpOnly refresh token cookie.
   * The browser automatically sends the cookie with the request.
   * @returns Observable of session response with new access token
   */
  refreshSession(): Observable<SessionResponse> {
    return this.http.post<SessionResponse>(
      `${this.authUrl}/refresh`,
      {},
      { withCredentials: true },
    );
  }

  /**
   * Refreshes the authentication token using the JWT ID.
   * Falls back to cookie-based refresh if jti is not provided.
   * @param jti - JWT ID from the current token (optional for cookie-based refresh)
   * @returns Observable of new credential token
   */
  refreshToken(jti?: string): Observable<CredentialToken> {
    if (jti) {
      return this.http.get<CredentialToken>(`${this.authUrl}/refresh/${jti}`, {
        withCredentials: true,
      });
    }
    // Use cookie-based refresh
    return this.http
      .post<SessionResponse>(`${this.authUrl}/refresh`, {}, { withCredentials: true })
      .pipe(
        map((res) => ({
          access_token: res.accessToken,
          expires_in: res.expiresIn,
          token_type: 'Bearer',
        })),
      );
  }

  /**
   * Logs out the user by clearing the server-side session and HttpOnly cookie.
   * Also clears all cached data to ensure a clean state for the next user.
   * @returns Observable that completes when logout is successful
   */
  logout(): Observable<void> {
    return this.http
      .post<void>(`${this.authUrl}/logout`, {}, { withCredentials: true })
      .pipe(tap(() => this.cacheService.clearAllCaches()));
  }

  /**
   * Changes the user's password.
   * @param payload - Password change request containing current and new passwords
   * @returns Observable of success boolean
   */
  changePassword(payload: ChangePassword): Observable<boolean> {
    return this.http.patch(`${this.baseUrl}/password`, payload).pipe(map(() => true));
  }

  /**
   * Requests a password reset email for the given address.
   * The server always returns 200 regardless of whether the email exists,
   * so the response reveals nothing about which accounts exist.
   * @param email - The email address of the account to reset
   * @returns Observable that completes when the request is accepted
   */
  requestPasswordReset(email: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/password-reset/request`, { email });
  }

  /**
   * Completes a password reset using the single-use token from the reset email.
   * @param token - The reset token from the emailed link
   * @param newPassword - The new password
   * @param newPassword2 - Confirmation of the new password
   * @returns Observable of success boolean
   */
  completePasswordReset(
    token: string,
    newPassword: string,
    newPassword2: string,
  ): Observable<boolean> {
    return this.http
      .post(`${this.baseUrl}/password-reset/complete`, { token, newPassword, newPassword2 })
      .pipe(map(() => true));
  }
}
